import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { OrderFacade } from '@features/order/facades/order-facade/order.facade';
import { PollingFacade } from '@features/polling/store/polling.facade';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { PageTitleService } from '@rd/core/services/page-title/page-title.service';
import { orderItemFilter } from '@rd/helpers/order.helper';
import {
  SuccessButtonGroup,
  SuccessButtonStatus,
  TransactionStatus,
} from '@rd/types';
import {
  CashRedemptionOrderItem,
  OrderItemStatus,
  OrderItemType,
} from '@rd/types/order-item.type';
import { combineLatest } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';

@UntilDestroy()
@Component({
  selector: 'rd-rcv2-success',
  templateUrl: './success.component.html',
  styleUrls: ['./success.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RcV2SuccessComponent implements OnInit {
  TransactionStatus = TransactionStatus;

  @HostBinding('class.rd-rcv2-success') componentClass = true;

  orderId = this.activatedRoute.snapshot.paramMap.get('orderId')!;

  orderPolling$ = this.pollingFacade.getPolling(this.orderId);

  order$ = this.orderFacade.order$;

  transactionStatus$ = combineLatest([
    this.orderFacade.getOrderError$,
    this.orderPolling$,
    this.order$,
  ]).pipe(
    map(([orderError, orderPolling, order]) => {
      if (orderError) {
        return TransactionStatus.FAIL;
      }
      if (order) {
        return TransactionStatus.SUCCESS;
      }
      return orderPolling?.status ?? TransactionStatus.LOADING;
    }),
  );

  redeemCashLoyaltyProgram$ = combineLatest([
    this.order$,
    this.loyaltyProgramFacade.cashBackLoyaltyPrograms$,
  ]).pipe(
    first(
      ([order, cashBackLoyaltyPrograms]) =>
        !!order && cashBackLoyaltyPrograms.length > 0,
    ),
    map(([order, cashBackLoyaltyPrograms]) => {
      const orderLoyaltyProgramId = order?.orderItems.find(
        orderItemFilter<CashRedemptionOrderItem>(
          OrderItemType.CashRedemptionOrderItem,
        ),
      )?.attributes.data.loyaltyProgramId;

      return cashBackLoyaltyPrograms.find(
        ({ id }) => id === orderLoyaltyProgramId,
      );
    }),
    // Start with null so that all observables in vm$ have at least 1 emit
    // so combineLatest can work and show the UI
    startWith(null),
  );

  vm$ = combineLatest([
    this.order$,
    this.redeemCashLoyaltyProgram$,
    this.transactionStatus$,
  ]).pipe(
    map(([order, redeemCashLoyaltyProgram, transactionStatus]) => ({
      order,
      redeemCashLoyaltyProgram,
      transactionStatus,
      successButtonsStatus: order
        ? this.getSuccessButtonsStatus(order.fulfillmentStatus)
        : 'pending',
    })),
  );

  constructor(
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private redeemCashSettingService: RedeemCashSettingService,
    private pageTitleService: PageTitleService,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
    private orderFacade: OrderFacade,
    private pollingFacade: PollingFacade,
  ) {}

  ngOnInit(): void {
    this.pageTitleService.setPageTitle('redeem-cash-success');

    this.orderFacade.getOrder(this.orderId);

    this.sendAnalyticsData();
  }

  get successButtons(): SuccessButtonGroup {
    return this.redeemCashSettingService.successButtons;
  }

  private getSuccessButtonsStatus(
    fulfillmentStatus: OrderItemStatus,
  ): SuccessButtonStatus {
    switch (fulfillmentStatus) {
      case OrderItemStatus.Processing:
      case OrderItemStatus.Pending: {
        return 'pending';
      }
      case OrderItemStatus.Fulfilled: {
        return 'confirmed';
      }
      default: {
        return 'failed';
      }
    }
  }

  private sendAnalyticsData() {
    this.analyticsService.track({
      // @ts-ignore
      routeName: this.analyticsService.sitemap.redeemCash.success,
    });

    this.order$.pipe(first((order) => !!order)).subscribe((order) => {
      this.analyticsService.track({
        // @ts-ignore
        routeName: this.analyticsService.sitemap.redeemCash.successFullData,
        extra: {
          status:
            order?.fulfillmentStatus === OrderItemStatus.Processing
              ? OrderItemStatus.Pending
              : order?.fulfillmentStatus,
        },
      });
    });
  }
}
